import "./App.css";
import { useEffect, useState } from "react";
import api from "./lib/api";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppSettingsProvider } from "./context/AppSettingsContext";
import Login from "./pages/Login";
import DashboardLayout from "./pages/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import Counter from "./pages/Counter";
import Customers from "./pages/Customers";
import Rewards from "./pages/Rewards";
import Campaigns from "./pages/Campaigns";
import Collections from "./pages/Collections";
import CatalogPage from "./pages/CatalogPage";
import Staff from "./pages/Staff";
import WhatsAppPage from "./pages/WhatsApp";
import Transactions from "./pages/Transactions";
import PublicPortal from "./pages/PublicPortal";
import PublicRegister from "./pages/PublicRegister";
import PublicLanding, { PublicBranches, PublicFlyer } from "./pages/PublicLanding";
import PublicHome, { PublicNewsList, PublicNewsDetail, PublicIniziative, PublicProducts, PublicRaccoltaPunti, PublicContacts, PublicPrivacy, PublicContest } from "./pages/PublicHome";
import PublicStores from "./pages/PublicStores";
import PublicHelp from "./pages/PublicHelp";
import PublicGiftCard, { PublicGiftCardView } from "./pages/PublicGiftCard";
import GroupGift from "./pages/GroupGift";
import WalletRecover from "./pages/WalletRecover";
import ThemePreview from "./pages/ThemePreview";
import GiftCards from "./pages/GiftCards";
import HomeCms from "./pages/HomeCms";
import PublicBranchMenu from "./pages/PublicBranchMenu";
import Birthdays from "./pages/Birthdays";
import ClientHome from "./pages/ClientHome";
import ClientLogin from "./pages/ClientLogin";
import ResetPassword from "./pages/ResetPassword";
import AppLanding from "./pages/AppLanding"; // eslint-disable-line no-unused-vars
import CircuitLanding from "./pages/CircuitLanding";
import InitiativePage from "./pages/InitiativePage";
import LandingCms from "./pages/LandingCms";
import Reservations from "./pages/Reservations";
import AppSettings from "./pages/AppSettings";
import Coupons from "./pages/Coupons";
import Branches from "./pages/Branches";
import Reports from "./pages/Reports";
import Segments from "./pages/Segments";
import Statistics from "./pages/Statistics";
import AdvancedSearch from "./pages/AdvancedSearch";
import Products from "./pages/Products";
import Communications from "./pages/Communications";
import AuthorizationRequests from "./pages/AuthorizationRequests";
import CustomerOrders from "./pages/CustomerOrders";
import CustomerMessages from "./pages/CustomerMessages";
import Flyers from "./pages/Flyers";
import IssueCard from "./pages/IssueCard";
import CardCodes from "./pages/CardCodes";
import SpokiSettings from "./pages/SpokiSettings";
import PayPalSettings from "./pages/PayPalSettings";
import ElasticSync from "./pages/ElasticSync";
import ExternalApi from "./pages/ExternalApi";
import CrmDashboard from "./pages/CrmDashboard";
import CrmProducts from "./pages/CrmProducts";
import PromoFlyers from "./pages/PromoFlyers";
import PromoFlyerDetail from "./pages/PromoFlyerDetail";
import PromoFlyerCompare from "./pages/PromoFlyerCompare";
import PlatformAdmin from "./pages/PlatformAdmin";
import AdminNotifications from "./pages/AdminNotifications";
import AiAssistant from "./pages/AiAssistant";
import Concorsi from "./pages/Concorsi";
import Sponsors from "./pages/Sponsors";
import Manual from "./pages/Manual";
import Maintenance from "./pages/Maintenance";
import PlatformLanding from "./pages/PlatformLanding";
import CircuitsShowcase from "./pages/CircuitsShowcase";
import { API, setTenantSlug } from "./lib/api";
import axios from "axios";

// Prefisso di percorso = slug del circuito (es. nouvellefidelity.it/ete/...): riconosciuto una volta all'avvio.
// Senza prefisso (e senza ?t=) il club memorizzato viene azzerato → vale il dominio (club principale); eccezione: PWA installata.
const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
function useTenantBase() {
  const [base, setBase] = useState(undefined);
  useEffect(() => {
    const { pathname, search, hash } = window.location;
    const params = new URLSearchParams(search);
    const seg = (pathname.split("/")[1] || "").toLowerCase();
    const legacy = (params.get("t") || "").toLowerCase();
    if (legacy && legacy !== seg) {
      params.delete("t");
      const qs = params.toString();
      window.location.replace(`/${legacy}${pathname === "/" ? "/" : pathname}${qs ? `?${qs}` : ""}${hash}`);
      return;
    }
    const stored = (localStorage.getItem("kl_tenant") || "").toLowerCase();
    if (pathname === "/" && stored && isStandalone()) { window.location.replace(`/${stored}/`); return; }
    const reset = () => { setTenantSlug(""); setBase(""); };
    if (!seg || !/^[a-z0-9-]+$/.test(seg)) { reset(); return; }
    axios.get(`${API}/platform/public/slugs`)
      .then(({ data }) => { if ((data.slugs || []).includes(seg)) { setTenantSlug(seg); setBase(`/${seg}`); } else reset(); })
      .catch(reset);
  }, []);
  return base;
}
let _platformHost = null; // cache: la radice del dominio piattaforma (nouvellefidelity.it) mostra la vetrina dei circuiti
// Manifest PWA per circuito: installando l'app da /ete/... start_url e scope restano su quel club.
function PwaManifest({ base }) {
  useEffect(() => {
    const slug = localStorage.getItem("kl_tenant") || "";
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.href = `${API}/public/manifest.json${slug ? `?t=${slug}` : ""}`;
    const icon = document.querySelector('link[rel="apple-touch-icon"]');
    if (icon && slug) icon.href = `${API}/public/pwa-icon-192.png?t=${slug}`;
  }, [base]);
  return null;
}
function RootGate({ children }) {
  // Dominio aperto senza login: la radice "/" mostra la Home pubblica; le altre rotte restano protette (staff → /login)
  const { user, ready } = useAuth();
  const loc = useLocation();
  const [platformHost, setPlatformHost] = useState(_platformHost);
  useEffect(() => {
    if (platformHost !== null || user || loc.pathname !== "/") return;
    api.get("/tenant/info").then(({ data }) => { _platformHost = !!data.platform_host; setPlatformHost(_platformHost); }).catch(() => { _platformHost = false; setPlatformHost(false); });
  }, [platformHost, user, loc.pathname]);
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Caricamento…</div>;
  if (!user && loc.pathname === "/") {
    if (platformHost === null) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Caricamento…</div>;
    return platformHost ? <PlatformLanding /> : <PublicHome />;
  }
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function Protected({ children, adminOnly = false, perm = null }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return (
    <div className="min-h-screen flex items-center justify-center text-zinc-500">
      Caricamento…
    </div>
  );
  if (!user) return <Navigate to="/cliente" state={{ from: loc }} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/negato" replace />;
  // Fine-grained permission gate: admins always pass, staff must hold the perm
  if (perm && user.role !== "admin" && !(user.permissions || []).includes(perm)) {
    return <Navigate to="/negato" replace />;
  }
  return children;
}

function Forbidden() {
  const { user } = useAuth();
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center p-8 text-center" data-testid="forbidden-page">
      <div className="h-16 w-16 rounded-2xl bg-red-500/15 text-red-400 flex items-center justify-center mb-4">
        <span className="text-3xl">🚫</span>
      </div>
      <h1 className="font-heading text-3xl font-extrabold">Accesso non consentito</h1>
      <p className="text-zinc-400 mt-2 max-w-md">
        Il tuo profilo non ha i permessi per questa sezione. Chiedi all'amministratore
        di attivartelo dalla pagina Operatori.
      </p>
      {user && (
        <div className="text-[11px] text-zinc-500 mt-4">
          Loggato come <strong>{user.name}</strong> · ruolo {user.role}
        </div>
      )}
    </div>
  );
}

function App() {
  const tenantBase = useTenantBase();
  if (tenantBase === undefined) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Caricamento…</div>;
  return (
    <div className="App grain">
      <PwaManifest base={tenantBase} />
      <AppSettingsProvider>
        <AuthProvider>
          <BrowserRouter basename={tenantBase}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/platform" element={<PlatformAdmin />} />
              <Route path="/cliente" element={<ClientHome />} />
              <Route path="/cliente/accedi" element={<ClientLogin />} />
              <Route path="/reimposta-password/:token" element={<ResetPassword />} />
              <Route path="/home" element={<PublicHome />} />
              <Route path="/home/classic" element={<PublicLanding />} />
              <Route path="/home/news" element={<PublicNewsList />} />
              <Route path="/home/news/:id" element={<PublicNewsDetail />} />
              <Route path="/home/offerte" element={<PublicNewsList category="offerte" />} />
              <Route path="/home/ricette" element={<PublicNewsList category="ricette" />} />
              <Route path="/home/punti-vendita" element={<PublicStores />} />
              <Route path="/home/iniziative" element={<PublicIniziative />} />
              <Route path="/home/volantini" element={<PublicFlyer />} />
              <Route path="/home/prodotti" element={<PublicProducts />} />
              <Route path="/home/raccolta-punti" element={<PublicRaccoltaPunti />} />
              <Route path="/home/contatti" element={<PublicContacts />} />
              <Route path="/home/aiuto" element={<PublicHelp />} />
              <Route path="/home/gift-card" element={<PublicGiftCard />} />
              <Route path="/wallet" element={<WalletRecover />} />
              <Route path="/tema" element={<ThemePreview />} />
              <Route path="/gift/:code" element={<PublicGiftCardView />} />
              <Route path="/regalo/:code" element={<GroupGift />} />
              <Route path="/home/privacy" element={<PublicPrivacy />} />
              <Route path="/home/concorsi" element={<PublicContest />} />
              <Route path="/circuiti" element={<CircuitsShowcase />} />
              <Route path="/piattaforma" element={<PlatformLanding />} />
              <Route path="/scopri" element={<CircuitLanding />} />
              <Route path="/scopri/iniziativa/:slug" element={<InitiativePage />} />
              <Route path="/scopri/catalogo/:slug" element={<CatalogPage />} />
              <Route path="/home/sedi" element={<PublicBranches />} />
              <Route path="/registrati" element={<PublicRegister />} />
              <Route path="/richiedi-card" element={<PublicRegister />} />
              <Route path="/carta/:card" element={<PublicPortal />} />
              <Route path="/menu-sede/:branchId" element={<PublicBranchMenu />} />
              <Route
                path="/"
                element={
                  <RootGate>
                    <DashboardLayout />
                  </RootGate>
                }
              >
                <Route index element={<Protected perm="view_reports"><Dashboard /></Protected>} />
                <Route path="banco" element={<Protected perm="pos_earn_redeem"><Counter /></Protected>} />
                <Route path="rilascio-card" element={<Protected perm="pos_earn_redeem"><IssueCard /></Protected>} />
                <Route path="codici-carta" element={<Protected adminOnly><CardCodes /></Protected>} />
                <Route path="clienti" element={<Protected perm="manage_customers"><Customers /></Protected>} />
                <Route path="movimenti" element={<Protected perm="view_reports"><Transactions /></Protected>} />
                <Route path="prenotazioni" element={<Protected perm="manage_rewards"><Reservations /></Protected>} />
                <Route path="premi" element={<Protected perm="manage_rewards"><Rewards /></Protected>} />
                <Route path="campagne" element={<Protected perm="manage_campaigns"><Campaigns /></Protected>} />
                <Route path="raccolte" element={<Protected perm="manage_rewards"><Collections /></Protected>} />
                <Route path="coupon" element={<Protected perm="manage_coupons"><Coupons /></Protected>} />
                <Route path="cluster" element={<Protected perm="manage_customers"><Segments /></Protected>} />
                <Route path="sedi" element={<Protected adminOnly><Branches /></Protected>} />
                <Route path="report" element={<Protected perm="view_reports"><Reports /></Protected>} />
                <Route path="statistiche" element={<Protected perm="view_reports"><Statistics /></Protected>} />
                <Route path="ricerca" element={<Protected perm="manage_customers"><AdvancedSearch /></Protected>} />
                <Route path="prodotti" element={<Protected perm="manage_rewards"><Products /></Protected>} />
                <Route path="compleanni" element={<Protected perm="manage_coupons"><Birthdays /></Protected>} />
                <Route path="comunicazioni" element={<Protected perm="manage_broadcast_send"><Communications /></Protected>} />
                <Route path="autorizzazioni" element={<Protected adminOnly><AuthorizationRequests /></Protected>} />
                <Route path="ordini-cliente" element={<Protected perm="pos_earn_redeem"><CustomerOrders /></Protected>} />
                <Route path="messaggi" element={<Protected adminOnly><CustomerMessages /></Protected>} />
                <Route path="staff" element={<Protected perm="manage_staff"><Staff /></Protected>} />
                <Route path="whatsapp" element={<Protected adminOnly><WhatsAppPage /></Protected>} />
                <Route path="spoki" element={<Protected adminOnly><SpokiSettings /></Protected>} />
                <Route path="paypal" element={<Protected adminOnly><PayPalSettings /></Protected>} />
                <Route path="elastic" element={<Protected adminOnly><ElasticSync /></Protected>} />
                <Route path="integrazioni-esterne" element={<Protected adminOnly><ExternalApi /></Protected>} />
                <Route path="gift-card" element={<Protected><GiftCards /></Protected>} />
                <Route path="crm-dashboard" element={<Protected adminOnly><CrmDashboard /></Protected>} />
                <Route path="crm-prodotti" element={<Protected adminOnly><CrmProducts /></Protected>} />
                <Route path="volantini-promo" element={<Protected adminOnly><PromoFlyers /></Protected>} />
                <Route path="volantini-promo/confronto" element={<Protected adminOnly><PromoFlyerCompare /></Protected>} />
                <Route path="volantini-promo/:id" element={<Protected adminOnly><PromoFlyerDetail /></Protected>} />
                <Route path="notifiche" element={<Protected adminOnly><AdminNotifications /></Protected>} />
                <Route path="assistente-ai" element={<Protected adminOnly><AiAssistant /></Protected>} />
                <Route path="concorsi" element={<Protected><Concorsi /></Protected>} />
                <Route path="sponsor" element={<Protected adminOnly><Sponsors /></Protected>} />
                <Route path="manuale" element={<Protected><Manual /></Protected>} />
                <Route path="manutenzione" element={<Protected adminOnly><Maintenance /></Protected>} />
                <Route path="impostazioni" element={<Protected perm="manage_settings"><AppSettings /></Protected>} />
                <Route path="volantini" element={<Protected perm="manage_flyers"><Flyers /></Protected>} />
                <Route path="home-cms" element={<Protected perm="manage_settings"><HomeCms /></Protected>} />
                <Route path="landing-cms" element={<Protected perm="manage_settings"><LandingCms /></Protected>} />
                <Route path="negato" element={<Forbidden />} />
              </Route>
            </Routes>
            <Toaster theme="dark" position="top-right" richColors closeButton />
          </BrowserRouter>
        </AuthProvider>
      </AppSettingsProvider>
    </div>
  );
}

export default App;
